import axios from 'axios';

// Base URL for API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle responses and errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error.response?.data || error.message);
  }
);

// Authentication APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  updatePassword: (passwords) => api.put('/auth/updatepassword', passwords),
};

// Employee APIs
export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getStats: () => api.get('/employees/stats/overview'),
};

// Attendance APIs
export const attendanceAPI = {
  getAll: (params) => api.get('/attendance', { params }),
  getById: (id) => api.get(`/attendance/${id}`),
  getToday: () => api.get('/attendance/today'),
  getMy: (params) => api.get('/attendance/my', { params }),
  checkIn: (data) => api.post('/attendance/checkin', data),
  checkOut: () => api.post('/attendance/checkout'),
  create: (data) => api.post('/attendance', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  delete: (id) => api.delete(`/attendance/${id}`),
  getStats: (employeeId, params) => api.get(`/attendance/stats/${employeeId}`, { params }),
};

// Leave APIs
export const leaveAPI = {
  getAll: (params) => api.get('/leaves', { params }),
  getById: (id) => api.get(`/leaves/${id}`),
  apply: (data) => api.post('/leaves', data),
  update: (id, data) => api.put(`/leaves/${id}`, data),
  updateStatus: (id, data) => api.put(`/leaves/${id}/status`, data),
  delete: (id) => api.delete(`/leaves/${id}`),
  getBalance: (employeeId) => api.get(`/leaves/balance/${employeeId}`),
};

// Payroll APIs
export const payrollAPI = {
  getAll: (params) => api.get('/payroll', { params }),
  getById: (id) => api.get(`/payroll/${id}`),
  create: (data) => api.post('/payroll', data),
  update: (id, data) => api.put(`/payroll/${id}`, data),
  delete: (id) => api.delete(`/payroll/${id}`),
  process: (data) => api.post('/payroll/process', data),
  getStats: (year, month) => api.get(`/payroll/stats/${year}/${month}`),
};

// Task APIs
export const taskAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  addComment: (id, comment) => api.post(`/tasks/${id}/comment`, { comment }),
  updateProgress: (id, progress) => api.put(`/tasks/${id}/progress`, { progress }),
  getStats: () => api.get('/tasks/stats/overview'),
};

// Performance APIs
export const performanceAPI = {
  getAll: (params) => api.get('/performance', { params }),
  getById: (id) => api.get(`/performance/${id}`),
  create: (data) => api.post('/performance', data),
  update: (id, data) => api.put(`/performance/${id}`, data),
  delete: (id) => api.delete(`/performance/${id}`),
  submitFeedback: (id, feedback) => api.put(`/performance/${id}/feedback`, { feedback }),
  getStats: (employeeId) => api.get(`/performance/stats/${employeeId}`),
};

// Document APIs
export const documentAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  upload: (formData) => {
    return axios.post(`${API_URL}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  getCategories: () => api.get('/documents/categories/list'),
};

// Asset APIs
export const assetAPI = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  assign: (id, employeeId) => api.put(`/assets/${id}/assign`, { employeeId }),
  unassign: (id) => api.put(`/assets/${id}/unassign`),
  addMaintenance: (id, data) => api.post(`/assets/${id}/maintenance`, data),
  getStats: () => api.get('/assets/stats/overview'),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAnalytics: (params) => api.get('/dashboard/analytics', { params }),
};

// Role APIs
export const roleAPI = {
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  getPermissions: () => api.get('/roles/permissions/list'),
  checkPermission: (permission) => api.post('/roles/check-permission', { permission }),
};

// Daily Report APIs
export const dailyReportAPI = {
  getMyTasks: () => api.get('/daily-reports/my-tasks'),
  submit: (data) => api.post('/daily-reports', data),
  getMy: (params) => api.get('/daily-reports/my', { params }),
  getAll: (params) => api.get('/daily-reports', { params }),
  getById: (id) => api.get(`/daily-reports/${id}`),
  review: (id, data) => api.patch(`/daily-reports/${id}/review`, data),
  delete: (id) => api.delete(`/daily-reports/${id}`),
};

// Lead APIs
export const leadAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  assign: (id, employeeId) => api.put(`/leads/${id}/assign`, { employeeId }),
  bulkAssign: (leadIds, employeeId) => api.post('/leads/bulk-assign', { leadIds, employeeId }),
  updateStatus: (id, status) => api.put(`/leads/${id}/status`, { status }),
  addCommunication: (id, data) => api.post(`/leads/${id}/communication`, data),
  addFollowUp: (id, data) => api.post(`/leads/${id}/followup`, data),
  updateFollowUpStatus: (leadId, followupId, status) => api.put(`/leads/${leadId}/followup/${followupId}`, { status }),
  addNote: (id, data) => api.post(`/leads/${id}/note`, data),
  getByEmployee: (employeeId) => api.get(`/leads/assigned/${employeeId}`),
  getMyLeads: () => api.get('/leads/my-leads'),
  getStats: () => api.get('/leads/stats'),
  getUpcomingFollowUps: (days) => api.get('/leads/followups/upcoming', { params: { days } }),
};

export default api;
